import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { currentUser as queryCurrentUser } from '@/services/auth';
import SiderFooter from '@/components/SiderFooter';
import BrandSiderHeader from '@/components/BrandSiderHeader';
import { loginModal } from '@/components/LoginModal';
import { registerSetInitialState } from '@/utils/authState';
import {
  LayoutShellReadyMarker,
  RouteContentReadyMarker,
  RouteFailureMarker,
  RouteRenderBoundary,
} from '@/components/routeLoadingBoundary';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

import { isPublicPath } from '@/constants/auth';
import { ensureAppLocale } from '@/utils/i18n';

const isDev = process.env.NODE_ENV === 'development';

if (typeof window !== 'undefined') {
  ensureAppLocale();
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const result = await queryCurrentUser({
        skipErrorHandler: true,
      });
      // 返回 { data: CurrentUser, message: string }
      if (result?.data) {
        return result.data;
      }
      return undefined;
    } catch (_error) {
      return undefined;
    }
  };

  const { location } = history;
  // 尝试获取用户信息
  const currentUser = await fetchUserInfo();

  // 如果未登录且不在公开路径，延迟触发登录弹窗
  if (!currentUser && !isPublicPath(location.pathname)) {
    setTimeout(() => loginModal.show(), 100);
  }

  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 注册 setInitialState，供请求拦截器使用
  registerSetInitialState(setInitialState);

  return {
    // 隐藏顶部头部区域，使用纯侧边栏布局
    headerRender: false,
    // 隐藏侧边栏底部的操作区域（语言切换等）
    actionsRender: () => [],
    // 自定义侧边栏 Logo
    menuHeaderRender: (logo: any, _title: any, props: any) => (
      <BrandSiderHeader
        logoSrc={logo?.props?.src}
        logoAlt={logo?.props?.alt}
        title={String(initialState?.settings?.title || defaultSettings.title || 'AI Nexus')}
        collapsed={Boolean(props?.collapsed)}
      />
    ),
    // 侧边栏底部: 积分、升级、设置、用户
    menuFooterRender: (props: any) => {
      return <SiderFooter collapsed={props?.collapsed} />;
    },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录且不在公开路径，弹出登录弹窗
      if (!initialState?.currentUser && !isPublicPath(location.pathname)) {
        loginModal.show();
      }
    },
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      const pathname = history.location.pathname;

      return (
        <>
          {/**
            * 这里统一记录“真实业务内容已经挂载过”。
            *
            * 这样后续如果高层再次短暂触发 `src/loading.tsx`，
            * 就能通过延迟显示把全局骨架抑制掉，避免覆盖已经出现过的业务骨架。
            */}
          <LayoutShellReadyMarker>
            <RouteFailureMarker pathname={pathname}>
              <RouteRenderBoundary pathname={pathname}>
                <RouteContentReadyMarker>{children}</RouteContentReadyMarker>
              </RouteRenderBoundary>
            </RouteFailureMarker>
          </LayoutShellReadyMarker>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 开发环境使用代理，生产环境使用相对路径
  ...errorConfig,
};
